import { memo, useMemo } from "react"
import { format, formatDistance, type Locale } from "date-fns"
import { enUS, ja, zhTW } from "date-fns/locale"
import {
  Bot,
  Clock4,
  ListTodo,
  MessageCircleMore,
  MessageSquareWarning,
  ThumbsUp,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { ellipsis } from "@f2e/shared/utils"

import type { ThreadSummaryResponse } from "@/hooks/api"

import { getChannelSourceLabelKey } from "../history-source-labels"
import { formatLatestActorName } from "../latestActorName"

const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)

export interface ThreadListItemProps {
  thread: ThreadSummaryResponse
  selected?: boolean
  hasHistoricalMatch?: boolean
  onSelect: (thread: ThreadSummaryResponse) => void
}

const ThreadListItem = ({
  thread,
  selected = false,
  hasHistoricalMatch = false,
  onSelect,
}: ThreadListItemProps) => {
  const { t, i18n } = useTranslation()
  const dateFnsLocale = useMemo(() => {
    const localeMap: Record<string, Locale> = {
      en: enUS,
      "zh-TW": zhTW,
      ja,
    }
    return localeMap[i18n.resolvedLanguage || i18n.language] || enUS
  }, [i18n.language, i18n.resolvedLanguage])
  const helpfulCount = thread.feedback_conversation_counts.sys_helpful ?? 0
  const improveCount = thread.feedback_conversation_counts.sys_improve ?? 0
  const preview =
    thread.latest_message?.preview.trim() ||
    thread.latest_message_preview?.trim()
  const participantName =
    thread.participant_name?.trim() || thread.external_user_id?.trim()
  const channelType = thread.channel?.type || thread.channel_type
  const channelTypeLabelKey = getChannelSourceLabelKey(channelType)
  const channelTypeLabel = channelTypeLabelKey
    ? t(`historyList.sourceSelect.options.${channelTypeLabelKey}`)
    : channelType
  const channelName = thread.channel?.name?.trim()
  const sourceLabel =
    thread.source_type === "workspace_api_key"
      ? t("historyList.sourceSelect.workspaceApi")
      : channelName || channelTypeLabel
  const shouldAppendChannelType = Boolean(
    channelName &&
    channelTypeLabel &&
    !channelName
      .toLocaleLowerCase()
      .includes(channelTypeLabel.toLocaleLowerCase())
  )
  const conversationContext = [
    participantName,
    sourceLabel,
    shouldAppendChannelType ? channelTypeLabel : undefined,
  ]
    .filter(Boolean)
    .join(" · ")
  const activityAt =
    thread.latest_message?.created_at || thread.last_activity_at
  const latestActorName = formatLatestActorName({
    actor: thread.latest_message?.actor,
    actorName: thread.latest_message?.actor_name,
    participantName,
    agentName: thread.agent_name,
    getFallbackName: (actor) => t(`historyList.card.latestActor.${actor}`),
  })

  return (
    <button
      type="button"
      className={cn(
        "relative block w-full px-4 py-3 text-left transition-colors duration-150 motion-reduce:transition-none hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
        selected && "bg-muted/70 ring-1 ring-inset ring-border"
      )}
      data-thread-id={thread.id}
      data-selected={selected ? "true" : "false"}
      data-unread={thread.is_unread ? "true" : "false"}
      onClick={() => onSelect(thread)}
    >
      <div className="grid gap-1">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
          <div className="min-w-40 flex-1 pt-0.5">
            <div className="flex min-w-0 items-center gap-2">
              {thread.is_unread && (
                <>
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-blue-500 ring-2 ring-blue-500/15 dark:bg-blue-400 dark:ring-blue-400/20"
                    aria-hidden="true"
                  />
                  <span className="sr-only">
                    {t("historyList.thread.unread")}.{" "}
                  </span>
                </>
              )}
              <div
                className={cn(
                  "line-clamp-1 min-w-0 text-base leading-6",
                  thread.is_unread
                    ? "font-semibold text-foreground"
                    : "font-medium text-foreground/85"
                )}
              >
                {thread.title?.trim() || t("historyList.thread.untitled")}
              </div>
            </div>
          </div>
          {thread.needs_reply ? (
            <Badge className="max-w-52 whitespace-nowrap border border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <Clock4 className="mr-1 h-3 w-3" aria-hidden="true" />
              {t("historyList.card.waitingForHuman")}
              {thread.waiting_since && (
                <span className="ml-1 font-normal">
                  ·{" "}
                  {t("historyList.card.waiting", {
                    duration: formatDistance(
                      new Date(thread.waiting_since),
                      new Date(),
                      { locale: dateFnsLocale }
                    ),
                  })}
                </span>
              )}
            </Badge>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "inline-flex cursor-default items-center gap-1 text-xs tabular-nums",
                    thread.is_unread
                      ? "font-semibold text-blue-700 dark:text-blue-300"
                      : "text-muted-foreground"
                  )}
                >
                  {formatDistance(new Date(activityAt), new Date(), {
                    locale: dateFnsLocale,
                  })}{" "}
                  {t("history.proAccordion.time.ago")}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {format(new Date(activityAt), "yyyy-MM-dd HH:mm:ss")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {(conversationContext || thread.status === "pending") && (
          <div className="flex min-w-0 items-center gap-2">
            {conversationContext && (
              <div className="min-w-0 truncate text-xs font-medium text-muted-foreground">
                {conversationContext}
              </div>
            )}
            {thread.status === "pending" && (
              <Badge
                variant="outline"
                className="ml-auto shrink-0 gap-1 border-blue-200 bg-blue-50 font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/40"
              >
                <ListTodo className="h-3 w-3 shrink-0" aria-hidden="true" />
                {t("historyList.thread.pending")}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="mt-2">
        {preview ? (
          <div
            className={cn(
              "line-clamp-2 min-h-10 break-words text-sm leading-5",
              thread.is_unread
                ? "font-medium text-foreground/80"
                : "text-muted-foreground"
            )}
          >
            {thread.latest_message && (
              <>
                <span className="font-semibold text-foreground/80">
                  {latestActorName}
                  {t("historyList.card.speakerSeparator")}
                </span>{" "}
              </>
            )}
            <span>{preview}</span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("historyList.thread.noPreview")}
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-muted-foreground">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {thread.agent_name && (
            <Badge
              variant="outline"
              className="max-w-[min(100%,14rem)] border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300"
            >
              <Bot className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">
                {ellipsis(thread.agent_name, 20)}
              </span>
            </Badge>
          )}
          {thread.history_count > 1 && (
            <Badge variant="outline" className="font-normal">
              <MessageCircleMore
                className="h-3 w-3 shrink-0"
                aria-hidden="true"
              />
              {t("historyList.thread.sessionCount", {
                count: thread.history_count,
              })}
            </Badge>
          )}
          {thread.human_mode && (
            <Badge variant="secondary" className="font-normal">
              {t("historyList.humanModeSelect.active")}
            </Badge>
          )}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2.5">
          {helpfulCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-default items-center gap-1 font-medium text-green-700 dark:text-green-500">
                  <ThumbsUp className="h-3 w-3" aria-hidden="true" />
                  {helpfulCount}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("historyList.feedbackBadge.helpfulTooltip")}
              </TooltipContent>
            </Tooltip>
          )}
          {improveCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-default items-center gap-1 font-medium text-red-700 dark:text-red-500">
                  <MessageSquareWarning
                    className="h-3 w-3"
                    aria-hidden="true"
                  />
                  {improveCount}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("historyList.feedbackBadge.improveTooltip")}
              </TooltipContent>
            </Tooltip>
          )}
          <span className="shrink-0 font-medium tabular-nums text-foreground/65">
            {t("historyList.card.credits", {
              credits: formatCredits(thread.total_cost_credits),
            })}
          </span>
        </div>
      </div>

      {hasHistoricalMatch && thread.history_count > 1 && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          {t("historyList.thread.historicalMatch")}
        </div>
      )}
    </button>
  )
}

export default memo(ThreadListItem)
